import type { FaceGesture } from "@vladmandic/human";
import type { FaceDetectionResponse, ObjectDetectionResponse } from "@yujacorp/inference-ambassador";
import { HumanFaceDetection, TfjsCocoObjectDetection } from "@yujacorp/verity-machine-learning";
import { useAtomValue } from "jotai";
import React from "react";
import { useTranslation } from "react-i18next";
import ArrowIcon from "./Arrow.svg?react";
import styles from "./DetectionOverlay.module.css";
import {
	faceDetectionEnabledAtom,
	gazeDirectionEnabledAtom,
	gestureDetectionEnabledAtom,
	objectDetectionEnabledAtom,
} from "./DetectionOverlaySettings";
import { computeGazeDirection } from "./gestures";
import type { VersionedFaceDetection, VersionedObjectDetection } from "./hooks";
interface DetectionOverlayV1Props {
	faceDetection?: HumanFaceDetection;
	objectDetection?: TfjsCocoObjectDetection;
	styling: {
		isFullscreen: boolean;
	};
}

const capitalizeEachWord = (str: string) => {
	return str.replace(/\b\w/g, (match) => match.toUpperCase());
};

const project3DRotationTo2DAngle = (face: FaceDetectionResponse["faces"][number]) => {
	const { yaw, pitch } = face.rotation;
	const rotationMagnitude = Math.hypot(yaw, pitch);

	if (!Number.isFinite(rotationMagnitude) || rotationMagnitude < 1) {
		return null;
	}

	const angleRadians = Math.atan2(-pitch, yaw);
	return (angleRadians * 180) / Math.PI;
};

export const DetectionOverlayV1: React.FC<DetectionOverlayV1Props> = ({ faceDetection, objectDetection, styling }) => {
	const { t } = useTranslation();
	const faceDetectionEnabled = useAtomValue(faceDetectionEnabledAtom);
	const objectDetectionEnabled = useAtomValue(objectDetectionEnabledAtom);
	const gestureDetectionEnabled = useAtomValue(gestureDetectionEnabledAtom);

	return (
		<div
			id="detectionOverlay"
			className={`${styles.overlayContainer} ${styling.isFullscreen ? styles.fullscreen : ""}`}
		>
			{/* Face detection overlays */}
			{faceDetectionEnabled &&
				faceDetection &&
				!faceDetection.isEmpty &&
				faceDetection.data.map((detection, index) => (
					<div
						key={`face-${index}`}
						className={`${styles.boundingBox} ${styles.faceBbox}`}
						style={{
							left: `${detection.normalizedBbox.normalizedX * 100}%`,
							top: `${detection.normalizedBbox.normalizedY * 100}%`,
							width: `${detection.normalizedBbox.normalizedWidth * 100}%`,
							height: `${detection.normalizedBbox.normalizedHeight * 100}%`,
						}}
					>
						<div className={styles.labelContainer}>
							<div
								className={`${styles.labelBox} ${styles.faceLabelBox} ${styling.isFullscreen ? styles.fullscreen : ""}`}
							>
								<div className={styles.labelTitle}>
									{t("result.components.studentSelection.videoPlayer.overlay.faceDetection")}
								</div>
								{detection.enrichedData?.score && (
									<div className={styles.labelSubtitle}>
										{t("result.components.studentSelection.videoPlayer.overlay.score")}
										{` ${Math.round(detection.enrichedData.score * 100)}%`}
									</div>
								)}
								{gestureDetectionEnabled &&
									detection.enrichedData?.gestures &&
									detection.enrichedData.gestures.map(
										(gesture: FaceGesture, index: number): React.ReactNode => (
											<div className={styles.labelSubtitle} key={index}>
												{capitalizeEachWord(gesture)}
											</div>
										),
									)}
							</div>
						</div>
					</div>
				))}

			{/* Object detection overlays */}
			{objectDetectionEnabled &&
				objectDetection &&
				!objectDetection.isEmpty &&
				objectDetection?.data?.length > 0 &&
				objectDetection.data.map((detection, index) => (
					<div
						key={`object-${index}`}
						className={`${styles.boundingBox} ${styles.objectBbox}`}
						style={{
							left: `${detection.normalizedBbox.normalizedX * 100}%`,
							top: `${detection.normalizedBbox.normalizedY * 100}%`,
							width: `${detection.normalizedBbox.normalizedWidth * 100}%`,
							height: `${detection.normalizedBbox.normalizedHeight * 100}%`,
						}}
					>
						<div className={styles.labelContainer}>
							<div
								className={`${styles.labelBox} ${styles.objectLabelBox} ${styling.isFullscreen ? styles.fullscreen : ""}`}
							>
								{detection.enrichedData?.label && (
									<div className={styles.labelTitle}>{capitalizeEachWord(detection.enrichedData.label)}</div>
								)}
								{detection.enrichedData?.score && (
									<div className={styles.labelSubtitle}>
										{t("result.components.studentSelection.videoPlayer.overlay.score")}
										{` ${Math.round(detection.enrichedData.score * 100)}%`}
									</div>
								)}
							</div>
						</div>
					</div>
				))}
		</div>
	);
};

interface DetectionOverlayV2Props {
	faceDetection?: FaceDetectionResponse;
	objectDetection?: ObjectDetectionResponse;
	styling: {
		isFullscreen: boolean;
	};
}

export const DetectionOverlayV2: React.FC<DetectionOverlayV2Props> = ({ faceDetection, objectDetection, styling }) => {
	const { t } = useTranslation();
	const faceDetectionEnabled = useAtomValue(faceDetectionEnabledAtom);
	const objectDetectionEnabled = useAtomValue(objectDetectionEnabledAtom);
	const gestureDetectionEnabled = useAtomValue(gestureDetectionEnabledAtom);
	const gazeDirectionEnabled = useAtomValue(gazeDirectionEnabledAtom);

	return (
		<div
			id="detectionOverlay"
			className={`${styles.overlayContainer} ${styling.isFullscreen ? styles.fullscreen : ""}`}
		>
			{/* Face detection overlays */}
			{faceDetectionEnabled &&
				faceDetection &&
				faceDetection.faces.map((face, index) => {
					const gazeDirection = computeGazeDirection(face);
					const gazeAngle = project3DRotationTo2DAngle(face);

					return (
						<div
							key={`face-${index}`}
							className={`${styles.boundingBox} ${styles.faceBbox}`}
							style={{
								left: `${face.normalized_bbox.x1 * 100}%`,
								top: `${face.normalized_bbox.y1 * 100}%`,
								width: `${(face.normalized_bbox.x2 - face.normalized_bbox.x1) * 100}%`,
								height: `${(face.normalized_bbox.y2 - face.normalized_bbox.y1) * 100}%`,
							}}
						>
							{gazeDirectionEnabled && gazeAngle !== null && (
								<ArrowIcon
									className={styles.gazeDirectionArrowIcon}
									style={{
										// center at arrow root, zero the default angle, apply rotation, translate in direction of arrow
										transform: `translate(-100%, 0%) rotate(${gazeAngle - 135}deg) translate(-25%, 25%)`,
										// the arrow is originally pointing down left, so the root is top right
										transformOrigin: "100% 0%",
									}}
								/>
							)}
							<div className={styles.labelContainer}>
								<div
									className={`${styles.labelBox} ${styles.faceLabelBox} ${styling.isFullscreen ? styles.fullscreen : ""}`}
								>
									<div className={styles.labelTitle}>
										{t("result.components.studentSelection.videoPlayer.overlay.faceDetection")}
									</div>
									{face.confidence && (
										<div className={styles.labelSubtitle}>
											{t("result.components.studentSelection.videoPlayer.overlay.score")}
											{` ${Math.round(face.confidence * 100)}%`}
										</div>
									)}
									{gestureDetectionEnabled && (
										<>
											<div className={styles.labelSubtitle}>{`Gaze Direction: ${gazeDirection}`}</div>
										</>
									)}
								</div>
							</div>
						</div>
					);
				})}

			{/* Object detection overlays */}
			{objectDetectionEnabled &&
				objectDetection &&
				objectDetection.objects.map((object, index) => (
					<div
						key={`object-${index}`}
						className={`${styles.boundingBox} ${styles.objectBbox}`}
						style={{
							left: `${object.normalized_bbox.x1 * 100}%`,
							top: `${object.normalized_bbox.y1 * 100}%`,
							width: `${(object.normalized_bbox.x2 - object.normalized_bbox.x1) * 100}%`,
							height: `${(object.normalized_bbox.y2 - object.normalized_bbox.y1) * 100}%`,
						}}
					>
						<div className={styles.labelContainer}>
							<div
								className={`${styles.labelBox} ${styles.objectLabelBox} ${styling.isFullscreen ? styles.fullscreen : ""}`}
							>
								<div className={styles.labelTitle}>{capitalizeEachWord(object.label)}</div>
								<div className={styles.labelSubtitle}>
									{t("result.components.studentSelection.videoPlayer.overlay.score")}
									{` ${Math.round(object.confidence * 100)}%`}
								</div>
							</div>
						</div>
					</div>
				))}
		</div>
	);
};

export interface DetectionOverlayProps {
	faceDetection?: VersionedFaceDetection;
	objectDetection?: VersionedObjectDetection;
	styling: {
		isFullscreen: boolean;
	};
}

export const DetectionOverlay = ({ faceDetection, objectDetection, styling }: DetectionOverlayProps) => {
	if (!faceDetection && !objectDetection) {
		return null;
	}

	if (faceDetection?.version === "v2") {
		return (
			<DetectionOverlayV2
				faceDetection={faceDetection as FaceDetectionResponse | undefined}
				objectDetection={objectDetection as ObjectDetectionResponse | undefined}
				styling={styling}
			/>
		);
	} else if (faceDetection || objectDetection) {
		return (
			<DetectionOverlayV1
				faceDetection={faceDetection as HumanFaceDetection | undefined}
				objectDetection={objectDetection as TfjsCocoObjectDetection | undefined}
				styling={styling}
			/>
		);
	}
	return null;
};
